import { useState } from "react";
import { Mail, Send, Loader2, CheckCircle2, Phone } from "lucide-react";
import LegalLayout from "./legal/LegalLayout";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [organisation, setOrganisation] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || message.trim().length < 10) {
            toast.error("Renseignez votre nom, un e-mail valide et un message d'au moins 10 caractères.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/contact", { name, email, organisation, message });
            setDone(true);
        } catch (err) {
            const status = err.response?.status;
            const detail = formatApiErrorDetail(err.response?.data?.detail);
            if (status === 429) {
                toast.error(detail || "Trop de messages envoyés récemment. Réessayez dans 1 heure ou écrivez directement à contact@profawz.fr.");
            } else if (!err.response) {
                toast.error("Connexion impossible. Vérifiez votre réseau ou écrivez à contact@profawz.fr.");
            } else {
                toast.error(detail || `Erreur d'envoi (HTTP ${status}). Écrivez à contact@profawz.fr.`);
            }
        } finally { setLoading(false); }
    };

    return (
        <LegalLayout title="Nous contacter" eyebrow="Une question ?">
            <p className="text-base text-ink/80 leading-relaxed">
                Une question sur la plateforme, l'abonnement, l'utilisation par un de vos formateurs ? Écrivez-nous et nous répondons sous <strong>48 heures ouvrées</strong> maximum.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
                <div className="panel p-4">
                    <Mail size={18} className="text-orange mb-2" />
                    <div className="label-eyebrow">E-mail direct</div>
                    <a href="mailto:contact@profawz.fr" className="text-sm font-mono text-ink hover:text-orange">contact@profawz.fr</a>
                </div>
                <div className="panel p-4">
                    <Phone size={18} className="text-orange mb-2" />
                    <div className="label-eyebrow">Support technique</div>
                    <p className="text-xs text-muted2">Réponse sous 48h ouvrées via le formulaire ou par e-mail.</p>
                </div>
            </div>

            {done ? (
                <div className="panel p-8 text-center mt-8" data-testid="contact-done">
                    <CheckCircle2 size={42} className="text-emerald-500 mx-auto mb-3" />
                    <h2 className="h-display text-2xl font-semibold">Message envoyé</h2>
                    <p className="text-sm text-muted2 mt-2 max-w-md mx-auto">
                        Merci ! Votre message est arrivé. Nous vous répondons sur <strong>{email}</strong> sous 48 heures ouvrées.
                    </p>
                </div>
            ) : (
                <form onSubmit={submit} className="panel p-6 mt-8 space-y-4" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-eyebrow mb-2 block">Nom complet *</label>
                            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="contact-name-input" />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">E-mail *</label>
                            <input type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="contact-email-input" />
                        </div>
                    </div>
                    <div>
                        <label className="label-eyebrow mb-2 block">Établissement / Organisation</label>
                        <input className="field-input" value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Lycée XYZ, CFA ABC…" data-testid="contact-org-input" />
                    </div>
                    <div>
                        <label className="label-eyebrow mb-2 block">Message *</label>
                        <textarea
                            rows={6}
                            className="field-input resize-none"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Votre question, votre besoin, votre suggestion…"
                            required
                            data-testid="contact-message-input"
                        />
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary !py-3" data-testid="contact-submit-button">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        {loading ? "Envoi…" : "Envoyer le message"}
                    </button>
                    <p className="text-[11px] text-muted2">
                        En envoyant ce message, vous acceptez que votre nom et votre adresse e-mail soient utilisés uniquement pour vous répondre. Voir notre{" "}
                        <a href="/confidentialite" className="text-orange hover:underline">politique de confidentialité</a>.
                    </p>
                </form>
            )}
        </LegalLayout>
    );
}
